import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { processPayment } from "./paymentstore";
import './events.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  
  const year = parts[0];
  const monthIndex = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  
  const dateObj = new Date(year, monthIndex, day);
  const month = dateObj.toLocaleString('en-US', { month: 'long' });
  
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  
  return `${getOrdinal(day)} ${month}, ${year}`;
};

export default function Events() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestiPage, setCurrentTestiPage] = useState(0);
  
  // Stats from Firestore
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [totalCities, setTotalCities] = useState(0);

  const [filter, setFilter] = useState('Campus');
  
  // Registration Modal State
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [regData, setRegData] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Recap Modal State
  const [selectedRecapEvent, setSelectedRecapEvent] = useState(null);
  const [showRecapModal, setShowRecapModal] = useState(false);

  // Subscription State
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Interest Form State
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestQuestions, setInterestQuestions] = useState([]);
  const [interestAnswers, setInterestAnswers] = useState({});
  const [interestEmail, setInterestEmail] = useState('');
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [currentInterestStep, setCurrentInterestStep] = useState(0);

  // Event Reviews State
  const [eventReviews, setEventReviews] = useState({});

  // Cinematic Video Player State
  const [playingVideoUrl, setPlayingVideoUrl] = useState(null);
  const reviewsCarouselRef = useRef(null);

  const scrollReviews = (direction) => {
    if (reviewsCarouselRef.current) {
      const scrollAmount = 340;
      reviewsCarouselRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };
  
  // Video Durations State
  const [videoDurations, setVideoDurations] = useState({});

  const handleLoadedMetadata = (e, url) => {
    const duration = Math.round(e.target.duration);
    if (!isNaN(duration)) {
      const minutes = Math.floor(duration / 60);
      const seconds = duration % 60;
      const formatted = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
      setVideoDurations(prev => ({ ...prev, [url]: formatted }));
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "events"));
        const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
        setEvents(fetched);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };
    
    const fetchTestimonialsAndReviews = async () => {
      try {
        const [testiSnap, reviewsSnap] = await Promise.all([
          getDocs(collection(db, "testimonials")),
          getDocs(collection(db, "event_reviews"))
        ]);

        const testiData = testiSnap.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
        
        const revData = reviewsSnap.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
        const approvedReviews = revData.filter(r => r.status === "approved");
        
        const mappedReviews = approvedReviews.map(r => ({
          dbId: r.dbId,
          authorName: r.userName,
          rating: r.rating,
          text: r.reviewText,
          eventName: r.eventName
        }));
        
        const combined = [...testiData, ...mappedReviews];
        setTestimonials(combined);

        const reviewsMap = {};
        approvedReviews.forEach(rev => {
          if (!reviewsMap[rev.eventId]) reviewsMap[rev.eventId] = [];
          reviewsMap[rev.eventId].push(rev);
        });
        setEventReviews(reviewsMap);
      } catch (error) {
        if (error.code === 'permission-denied') {
           console.warn("Reviews hidden: Please log in to see attendee reviews.");
        } else {
           console.error("Error fetching testimonials or reviews:", error);
        }
      }
    };

    const fetchStats = async () => {
      try {
        const statsDoc = await getDoc(doc(db, "metadata", "events_metadata"));
        if (statsDoc.exists()) {
          const data = statsDoc.data();
          setTotalEvents(data.totalEvents || 0);
          setTotalAttendees(data.totalAttendees || 0);
          setTotalCities(data.totalCities || 0);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchEvents();
    fetchStats();
    fetchTestimonialsAndReviews();
  }, []);

  const filters = ['Campus', 'Conversations', 'Connections' ];

  const filteredEvents = events.filter(e => {
    const evTopics = (e.topics || '').toLowerCase();
    const f = filter.toLowerCase().trim();
    return evTopics.includes(f) || evTopics === f;
  });

  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const localDateStr = `${year}-${month}-${day}`;
  
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const localTimeStr = `${hours}:${minutes}`;

  const getComparableDate = (dStr) => {
    if (!dStr) return "";
    let clean = String(dStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
    
    // Check for DD/MM/YYYY or DD-MM-YYYY
    const dmRegex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/;
    const dmMatch = clean.match(dmRegex);
    if (dmMatch) {
      const p1 = parseInt(dmMatch[1], 10);
      const p2 = parseInt(dmMatch[2], 10);
      const y = dmMatch[3];
      if (p1 > 12) {
        return `${y}-${String(p2).padStart(2, '0')}-${String(p1).padStart(2, '0')}`;
      } else {
        return `${y}-${String(p1).padStart(2, '0')}-${String(p2).padStart(2, '0')}`;
      }
    }

    const d = new Date(clean);
    if (!isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    }
    return clean;
  };

  const happeningNowEvents = filteredEvents.filter(e => {
    const evDate = getComparableDate(e.date);
    if (evDate === localDateStr) {
      if (e.startTime && e.endTime) {
         return localTimeStr >= e.startTime && localTimeStr <= e.endTime;
      }
      return true;
    }
    return false;
  });

  const upcomingEvents = filteredEvents.filter(e => {
    const evDate = getComparableDate(e.date);
    if (!evDate || evDate > localDateStr) return true;
    if (evDate === localDateStr && e.startTime && localTimeStr < e.startTime) return true;
    return false;
  });

  const pastEvents = events.filter(e => {
    const evDate = getComparableDate(e.date);
    if (evDate && evDate < localDateStr) return true;
    if (evDate === localDateStr && e.endTime && localTimeStr > e.endTime) return true;
    return false;
  });

  const displayPastEvents = [...pastEvents].sort((a, b) => {
    const dateA = getComparableDate(a.date) || "";
    const dateB = getComparableDate(b.date) || "";
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA); // most recent first (descending)
    }
    const timeA = a.endTime || "";
    const timeB = b.endTime || "";
    return timeB.localeCompare(timeA); // if same date, later time first
  });

  const handleRegisterClick = (ev) => {
    if (!auth?.currentUser) {
      alert("Please log in to register for an event.");
      return;
    }
    setSelectedEvent(ev);
    setShowModal(true);
    setRegData({ name: '', email: '', phone: '' });
  };

  const handleRecapClick = (ev) => {
    setSelectedRecapEvent(ev);
    setShowRecapModal(true);
  };

  const handleSubscribeSubmit = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) return;
    setIsSubscribing(true);
    try {
      await addDoc(collection(db, "newsletter_subscribers"), {
        email: subscribeEmail,
        timestamp: serverTimestamp()
      });
      alert('Successfully subscribed to newsletter!');
      setSubscribeEmail('');
    } catch (error) {
      console.error("Error subscribing:", error);
      alert('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleInterestClick = async () => {
    if (!auth?.currentUser) {
      alert("Please log in to fill out the interest form.");
      return;
    }
    setShowInterestModal(true);
    setInterestAnswers({});
    setInterestEmail('');
    try {
      const docRef = await getDoc(doc(db, "metadata", "interest_form"));
      if (docRef.exists() && docRef.data().questions) {
        setInterestQuestions(docRef.data().questions);
      } else {
        setInterestQuestions(["What topics are you most interested in?"]);
      }
    } catch (error) {
      console.error("Failed to fetch interest form:", error);
      setInterestQuestions(["What topics are you most interested in?"]);
    }
  };

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingInterest(true);
    try {
      await addDoc(collection(db, "interest_submissions"), {
        userId: auth?.currentUser ? auth.currentUser.uid : null,
        userEmail: auth?.currentUser ? auth.currentUser.email : interestEmail,
        answers: interestAnswers,
        submittedAt: serverTimestamp()
      });
      alert('Thank you for sharing your interests!');
      setShowInterestModal(false);
    } catch (error) {
      console.error("Error submitting interests:", error);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setIsSubmitting(true);

    const costString = selectedEvent.eventCost || "0";
    const parsedPrice = parseFloat(costString.replace(/[^0-9.]/g, '')) || 0;

    const backendOrderId = `EVT_${Date.now()}`;
    const userId = auth?.currentUser ? auth.currentUser.uid : "guest";

    if (parsedPrice > 0) {
      try {
        const response = await fetch('https://us-central1-nabi-1708b.cloudfunctions.net/createRazorpayOrder', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ amount: parsedPrice, bookingId: backendOrderId, type: 'event' })
        });
        const data = await response.json();
        
        if (!data.success) {
            throw new Error(data.error || "Failed to create Razorpay Order");
        }

        processPayment({
          price: parsedPrice,
          orderId: data.order_id,
          userData: {
            fullName: regData.name,
            email: regData.email,
            contactNumber: regData.phone
          },
          onSuccess: async (res) => {
            const paymentId = res.razorpay_payment_id;
            try {
              await addDoc(collection(db, "event_registrations"), {
                eventId: selectedEvent.dbId,
                eventTitle: selectedEvent.title,
                name: regData.name,
                email: regData.email,
                phone: regData.phone,
                amountPaid: parsedPrice,
                paymentId: paymentId,
                orderId: backendOrderId,
                userId: userId,
                timestamp: serverTimestamp()
              });
              alert("Successfully registered and paid for the event!");
              setShowModal(false);
              setSelectedEvent(null);
            } catch (error) {
              console.error("Error saving registration:", error);
              alert("Payment successful, but failed to save registration.");
            } finally {
              setIsSubmitting(false);
            }
          },
          onFailure: (error) => {
            alert("❌ Payment Failed or Cancelled.");
            setIsSubmitting(false);
            try {
              addDoc(collection(db, "payment_audit"), {
                userId: userId,
                userEmail: regData.email,
                action: "EVENT_PAYMENT_FAILED",
                amount: parsedPrice,
                status: "failed",
                error: error || "Payment modal dismissed",
                eventId: selectedEvent.dbId,
                timestamp: serverTimestamp()
              });
            } catch (auditError) {
              console.error("Failed to log event payment failure:", auditError);
            }
          }
        });

      } catch (error) {
        console.error("Error creating payment order:", error);
        alert("Failed to initiate payment. Please try again.");
        setIsSubmitting(false);
      }

    } else {
      // Free event
      try {
        await addDoc(collection(db, "event_registrations"), {
          eventId: selectedEvent.dbId,
          eventTitle: selectedEvent.title,
          name: regData.name,
          email: regData.email,
          phone: regData.phone,
          amountPaid: 0,
          userId: userId,
          timestamp: serverTimestamp()
        });
        alert("Successfully registered for the free event!");
        setShowModal(false);
        setSelectedEvent(null);
      } catch (error) {
        console.error("Error registering:", error);
        alert("Failed to register. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="events-page">
      <div className="events-container">
        
        {/* HERO SECTION */}
        <div className="events-hero-section">
          {happeningNowEvents.length > 0 && (
            <div className="hero-badge">
              <span className="live-dot"></span> LIVE NOW
            </div>
          )}
          <div className="hero-title">Space to be  
          </div>
          <p className="hero-subtitle">
           To reflect, share stories and connect.
          </p>
          <div className="hero-actions">
            <button className="btn-join-live" onClick={() => document.getElementById('upcoming-events').scrollIntoView({ behavior: 'smooth' })}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '6px'}}><path d="M8 5v14l11-7z"/></svg>
              Explore Upcoming 
            </button>
            <button className="btn-explore" onClick={() => {
              const el = document.getElementById('past-events');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
               Explore Past Events
            </button>
            
            <button className="btn-explore" onClick={handleInterestClick}>
              Interested In
            </button>
          </div>
          <div className="hero-stats-bar">
            <div className="stat-box">
              <h2>{totalEvents > 0 ? totalEvents + '+' : '0'}</h2>
              <p>Events hosted</p>
            </div>
            <div className="stat-box">
              <h2>{totalAttendees > 1000 ? (totalAttendees/1000).toFixed(1) + 'k' : totalAttendees}</h2>
              <p>Total attendees</p>
            </div>
            <div className="stat-box">
              <h2>{totalCities}</h2>
              <p>Cities</p>
            </div>
          </div>
        </div>

        {/* HAPPENING NOW SECTION */}
        {happeningNowEvents.length > 0 && (
          <div id="happening-now" className="happening-now-section">
            <div className="events-label">
              <span className="events-icon">⚡</span>
              <span className="events-label-text">HAPPENING NOW</span>
            </div>
            {happeningNowEvents.map(ev => {
              const topicsList = ev.topics ? ev.topics.split(',').map(t => t.trim()).filter(t => t) : [];
              return (
                <div className="happening-card" key={ev.dbId} style={{ backgroundImage: `url('${ev.img || '/placeholder.jpg'}')` }}>
                  <div className="happening-overlay">
                    <div className="happening-badge"><span className="live-dot"></span> LIVE NOW</div>
                    <div className="happening-tags">
                      {topicsList.map((t, i) => <span key={i} className="happening-tag">{t}</span>)}
                    </div>

                    
                    <h2 className="happening-title">{ev.title}</h2>
                    <p className="happening-subtitle">The premier gathering of industry experts. Join us live.</p>
                    <div className="happening-meta">
                      <span>📅 {formatDate(ev.date)}</span>
                      <span>⏰ {ev.startTime} - {ev.endTime}</span>
                      <span>📍 {ev.location}</span>
                    </div>
                    <div className="happening-footer" style={{ display: 'flex', alignItems: 'center', gap: '25px', marginTop: '10px' }}>
                      <button className="happening-join-btn" onClick={() => handleRegisterClick(ev)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{marginRight: '6px'}}><path d="M8 5v14l11-7z"/></svg>
                        Join the Event
                      </button>
                      <div className="happening-progress">
                         <span style={{color: '#ccc', fontSize: '11px', display: 'flex', alignItems: 'center'}}>
                           <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '5px'}}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                           3,840 attending / 5,000 capacity <span style={{color: '#f29053', fontWeight: 'bold', marginLeft: '5px'}}>77% full</span>
                         </span>
                         <div style={{ width: '220px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', marginTop: '8px' }}>
                           <div style={{ width: '77%', height: '100%', background: '#f29053', borderRadius: '2px' }}></div>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

                {upcomingEvents.length > 0 ? (
          <>
{/* UPCOMING EVENTS SECTION */}
        <div id="upcoming-events" className="events-header-section" style={{ marginTop: '80px' }}>
          <div className="events-label">
            <span className="events-icon">📅</span>
            <span className="events-label-text">UPCOMING EVENTS</span>
          </div>
          <div className="events-title-row">
            <h2 className="events-main-title">Reserve Your Spot</h2>
            <div className="events-filters">
              {filters.map(f => (
                <button 
                  key={f} 
                  className={`events-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="events-grid">
          {upcomingEvents.length === 0 ? (
            <p className="no-events-text">Watch this space for upcoming events.</p>
          ) : (
            upcomingEvents.map(ev => {
              const topicsList = ev.topics ? ev.topics.split(',').map(t => t.trim()).filter(t => t) : [];
              const firstTopic = topicsList.length > 0 ? topicsList[0] : 'Event';
              
              return (
                <div className="event-card" key={ev.dbId}>
                  <div className="event-card-img-wrap">
                    <img src={ev.img || '/placeholder.jpg'} alt={ev.title} className="event-card-img" />
                    <span className="event-badge-top-left">{firstTopic}</span>
                    <span className="event-badge-top-right">Spots Available</span>
                  </div>
                  <div className="event-card-body">
                    <div className="event-card-tags">
                      {topicsList.slice(0, 2).map((t, i) => (
                        <span key={i} className="event-tag">{t}</span>
                      ))}
                    </div>
                    <h3 className="event-card-title">{ev.title}</h3>
                    <div className="event-meta-info">
                      <div className="event-meta-item">
                        <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        <span>{formatDate(ev.date)} • {ev.startTime} - {ev.endTime}</span>
                      </div>
                      <div className="event-meta-item">
                        <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        <span>{ev.location}</span>
                      </div>
                    </div>
                    <div className="event-card-footer">
                      <span className="event-cost">{ev.eventCost || 'Free'}</span>
                      <button className="event-register-btn" onClick={() => handleRegisterClick(ev)}>
                        Register →
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* PAST EVENTS SECTION */}
        <div id="past-events" className="events-header-section" style={{ marginTop: '80px' }}>
          <div className="events-label">
            <span className="events-icon">🕒</span>
            <span className="events-label-text">PAST EVENTS</span>
          </div>
        </div>

        <div className="past-events-grid">
          {displayPastEvents.map((ev, index) => {
            const topicsList = ev.topics ? ev.topics.split(',').map(t => t.trim()).filter(t => t) : [];
            const cardClass = index === 0 ? 'past-event-card large' : index === 1 ? 'past-event-card medium' : 'past-event-card small';
            
            return (
              <div className={cardClass} key={ev.dbId}>
                <div className="past-event-img-wrap">
                  <img src={ev.img || '/placeholder.jpg'} alt={ev.title} className="past-event-img" />
                  <div className="past-event-overlay">
                    <div className="past-event-tags">
                      {topicsList.map((t, i) => (
                        <span key={i} className="past-event-tag">{t}</span>
                      ))}
                    </div>
                    <h3 className="past-event-title">{ev.title}</h3>
                  </div>
                </div>
                <div className="past-event-footer">
                  <div className="past-event-meta">
                    <span className="past-meta-item">
                      <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                      {formatDate(ev.date)}
                    </span>
                    <span className="past-meta-item">
                      <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                      {ev.location}
                    </span>

                  </div>
                  {(ev.recapText || (ev.recapMediaUrls && ev.recapMediaUrls.length > 0) || ev.recapImg) && (
                    <button className="recap-btn" onClick={() => handleRecapClick(ev)}>
                      More info
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft: '4px'}}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
                </>
        ) : (
          <>
{/* PAST EVENTS SECTION */}
        <div id="past-events" className="events-header-section" style={{ marginTop: '80px' }}>
          <div className="events-label">
            <span className="events-icon">🕒</span>
            <span className="events-label-text">PAST EVENTS</span>
          </div>
        </div>

        <div className="past-events-grid">
          {displayPastEvents.map((ev, index) => {
            const topicsList = ev.topics ? ev.topics.split(',').map(t => t.trim()).filter(t => t) : [];
            const cardClass = index === 0 ? 'past-event-card large' : index === 1 ? 'past-event-card medium' : 'past-event-card small';
            
            return (
              <div className={cardClass} key={ev.dbId}>
                <div className="past-event-img-wrap">
                  <img src={ev.img || '/placeholder.jpg'} alt={ev.title} className="past-event-img" />
                  <div className="past-event-overlay">
                    <div className="past-event-tags">
                      {topicsList.map((t, i) => (
                        <span key={i} className="past-event-tag">{t}</span>
                      ))}
                    </div>
                    <h3 className="past-event-title">{ev.title}</h3>
                  </div>
                </div>
                <div className="past-event-footer">
                  <div className="past-event-meta">
                    <span className="past-meta-item">
                      <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                      {formatDate(ev.date)}
                    </span>
                    <span className="past-meta-item">
                      <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                      {ev.location}
                    </span>

                  </div>
                  {(ev.recapText || (ev.recapMediaUrls && ev.recapMediaUrls.length > 0) || ev.recapImg) && (
                    <button className="recap-btn" onClick={() => handleRecapClick(ev)}>
                      More info
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft: '4px'}}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      {/* UPCOMING EVENTS SECTION */}
        <div id="upcoming-events" className="events-header-section" style={{ marginTop: '80px' }}>
          <div className="events-label">
            <span className="events-icon">📅</span>
            <span className="events-label-text">UPCOMING EVENTS</span>
          </div>
          <div className="events-title-row">
            <h2 className="events-main-title">Reserve Your Spot</h2>
            <div className="events-filters">
              {filters.map(f => (
                <button 
                  key={f} 
                  className={`events-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="events-grid">
          {upcomingEvents.length === 0 ? (
            <p className="no-events-text">Watch this space for upcoming events.</p>
          ) : (
            upcomingEvents.map(ev => {
              const topicsList = ev.topics ? ev.topics.split(',').map(t => t.trim()).filter(t => t) : [];
              const firstTopic = topicsList.length > 0 ? topicsList[0] : 'Event';
              
              return (
                <div className="event-card" key={ev.dbId}>
                  <div className="event-card-img-wrap">
                    <img src={ev.img || '/placeholder.jpg'} alt={ev.title} className="event-card-img" />
                    <span className="event-badge-top-left">{firstTopic}</span>
                    <span className="event-badge-top-right">Spots Available</span>
                  </div>
                  <div className="event-card-body">
                    <div className="event-card-tags">
                      {topicsList.slice(0, 2).map((t, i) => (
                        <span key={i} className="event-tag">{t}</span>
                      ))}
                    </div>
                    <h3 className="event-card-title">{ev.title}</h3>
                    <div className="event-meta-info">
                      <div className="event-meta-item">
                        <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                        <span>{formatDate(ev.date)} • {ev.startTime} - {ev.endTime}</span>
                      </div>
                      <div className="event-meta-item">
                        <svg className="event-meta-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                        <span>{ev.location}</span>
                      </div>
                    </div>
                    <div className="event-card-footer">
                      <span className="event-cost">{ev.eventCost || 'Free'}</span>
                      <button className="event-register-btn" onClick={() => handleRegisterClick(ev)}>
                        Register →
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

                  </>
        )}{/* TESTIMONIALS SECTION */}
      {(() => {
        const cardsPerPage = 3;
        const totalTestiPages = Math.max(1, Math.ceil(testimonials.length / cardsPerPage));
        
        const avgRating = testimonials.length > 0 
          ? testimonials.reduce((acc, t) => acc + (Number(t.rating) || 5), 0) / testimonials.length 
          : 5;
        const avgEmoji = avgRating >= 4.5 ? '😊' : avgRating >= 3.5 ? '🙂' : avgRating >= 2.5 ? '😐' : avgRating >= 1.5 ? '🙁' : '😠';

        const nextTestiPage = () => {
          setCurrentTestiPage((prev) => (prev + 1) % totalTestiPages);
        };

        const prevTestiPage = () => {
          setCurrentTestiPage((prev) => (prev - 1 + totalTestiPages) % totalTestiPages);
        };

        const visibleTestimonials = testimonials.slice(currentTestiPage * cardsPerPage, (currentTestiPage + 1) * cardsPerPage);

        return (
          <div className="testimonials-section">
          <div className="testimonials-header">
            <div className="testimonials-title-area">
              <div className="testimonials-label">
                <span className="testimonials-icon">💬</span>
                <span className="testimonials-label-text">ATTENDEE REVIEWS</span>
              </div>
              <h2 className="testimonials-main-title">What People Say</h2>
            </div>
            <div className="testimonials-stats">
              <div className="rating-score">
                <span className="rating-number">{avgEmoji}</span>
                <span className="rating-text">avg. rating</span>
              </div>
              <div className="testimonials-nav">
                <button className="nav-btn" onClick={prevTestiPage} style={{ opacity: totalTestiPages <= 1 ? 0.5 : 1, cursor: totalTestiPages <= 1 ? 'not-allowed' : 'pointer' }}>{"<"}</button>
                <button className="nav-btn" onClick={nextTestiPage} style={{ opacity: totalTestiPages <= 1 ? 0.5 : 1, cursor: totalTestiPages <= 1 ? 'not-allowed' : 'pointer' }}>{">"}</button>
              </div>
            </div>
          </div>
          
          <div className="testimonials-grid">
            {testimonials.length === 0 ? (
              <p style={{ textAlign: 'center', width: '100%', color: '#888', padding: '40px 0' }}>
                No reviews yet. Admin can add them from the dashboard.
              </p>
            ) : (
              visibleTestimonials.map((testi, idx) => {
                const initials = testi.authorName ? testi.authorName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'A';
                const rating = Number(testi.rating) || 5;
                const bgColors = ['#635BFF', '#f29053', '#F99B00', '#F07167'];
                const bgColor = bgColors[idx % bgColors.length];
                
                return (
                  <div className="testimonial-card" key={testi.dbId}>
                    <div className="testimonial-card-header">
                      <div className="testimonial-author-info">
                        <div className="testimonial-avatar" style={{backgroundColor: bgColor}}>
                          {initials}
                        </div>
                        <div className="testimonial-author-text">
                          <h4>{testi.authorName}</h4>
                          <p>{testi.authorTitle}</p>
                        </div>
                      </div>
                      <div className="testimonial-rating modern-rating">
                        <span className="rating-emoji">
                          {rating >= 5 ? '😊' : rating >= 4 ? '🙂' : rating === 3 ? '😐' : rating === 2 ? '🙁' : '😠'}
                        </span>
                      </div>
                    </div>
                    <div className="testimonial-body">
                      <p>"{testi.text}"</p>
                    </div>
                    <div className="testimonial-footer">
                      <span>↳ {testi.eventName}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* CAROUSEL INDICATORS */}
          {totalTestiPages > 1 && (
            <div className="carousel-indicators">
              {Array.from({ length: totalTestiPages }).map((_, index) => (
                <span 
                  key={index} 
                  className={`carousel-dot ${index === currentTestiPage ? 'active' : ''}`}
                  onClick={() => setCurrentTestiPage(index)}
                />
              ))}
            </div>
          )}
        </div>
        );
      })()}
      
      {/* NEWSLETTER SUBSCRIPTION SECTION */}
      <div className="eventsubscription">
        <div className="newsletter-eventcard">
          <span className="newsletter-label">STAY IN THE LOOP</span>
          <h2 className="newsletter-title">Never Miss an Event</h2>
          <p className="newsletter-subtitle">
           Stay connected with fresh updates, meaningful experiences, and inspiring opportunities. Subscribe today and receive the latest news directly in your inbox.
          </p>
          <form className="newsletter-form" onSubmit={handleSubscribeSubmit}>
            <input type="email" placeholder="your@email.com" required className="newsletter-input" value={subscribeEmail} onChange={(e) => setSubscribeEmail(e.target.value)} disabled={isSubscribing} />
            <button type="submit" className="newsletter-btn" disabled={isSubscribing}>
              {isSubscribing ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        </div>
      </div>

      {/* RECAP MODAL */}
      {showRecapModal && selectedRecapEvent && (
        <div className="event-modal-overlay" onClick={() => setShowRecapModal(false)}>
          <div className="event-modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '1000px', width: '90%' }}>
            <button className="event-modal-close" onClick={() => setShowRecapModal(false)}>✕</button>
            <div className="registration-header" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', paddingBottom: '24px', marginBottom: '10px' }}>
              <h2 style={{ color: '#003049', fontSize: '28px', fontWeight: '700', marginBottom: '12px' }}>Recap: {selectedRecapEvent.title}</h2>
              <div style={{ display: 'flex', gap: '20px', color: '#F07167', fontSize: '15px', fontWeight: '600' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  {formatDate(selectedRecapEvent.date)}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                  {selectedRecapEvent.location}
                </span>
              </div>
            </div>
            
            <div className="registration-body" style={{ padding: '30px' }}>
              {/* Media Carousel */}
              {(() => {
                let mediaUrls = selectedRecapEvent.recapMediaUrls || [];
                if (selectedRecapEvent.recapImg && !mediaUrls.includes(selectedRecapEvent.recapImg)) {
                   mediaUrls = [selectedRecapEvent.recapImg, ...mediaUrls];
                }
                
                const isVideo = (url) => {
                    if (!url) return false;
                    const cleanUrl = url.split('?')[0];
                    return cleanUrl.match(/\.(mp4|webm|ogg|mp3)$/i);
                };

                return (
                  <div className="recap-media-carousel" style={{ justifyContent: mediaUrls.length < 3 ? 'center' : 'flex-start' }}>
                    {mediaUrls.length > 0 ? mediaUrls.map((url, i) => {
                      const displayedDuration = videoDurations[url] || '...';
                      return (
                        <div className="recap-media-card" key={i} onClick={() => isVideo(url) ? setPlayingVideoUrl(url) : null} style={{ cursor: isVideo(url) ? 'pointer' : 'default' }}>
                          {isVideo(url) ? (
                            <video src={url} onLoadedMetadata={(e) => handleLoadedMetadata(e, url)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted loop autoPlay />
                          ) : (
                            <img src={url} alt={`Recap media ${i}`} />
                          )}
                          <div className="recap-media-overlay">
                             <div className="recap-media-top">
                               {isVideo(url) && (
                                 <span className="recap-media-tag">⏱ {displayedDuration}</span>
                               )}
                             </div>
                          </div>
                        </div>
                      )
                    }) : null}
                  </div>
                );
              })()}
              
              <div style={{ 
                fontSize: '15.5px', lineHeight: '1.7', color: '#333', whiteSpace: 'pre-wrap',
                background: '#fff', padding: '24px 30px', borderRadius: '12px',
                borderLeft: '4px solid #F07167', marginTop: '30px', 
                boxShadow: '0 4px 15px rgba(0,0,0,0.04)'
              }}>
                {selectedRecapEvent.recapText || "No detailed recap has been written for this event yet."}
              </div>

              {eventReviews[selectedRecapEvent.dbId] && eventReviews[selectedRecapEvent.dbId].length > 0 && (
                <div style={{ marginTop: '35px', paddingTop: '25px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <h3 style={{ fontSize: '20px', color: '#003049', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '24px' }}>💬</span> Attendee Reviews
                  </h3>
                  <style>{`
                    .recap-reviews-carousel::-webkit-scrollbar { display: none; }
                  `}</style>
                  <div style={{ position: 'relative' }}>
                    {eventReviews[selectedRecapEvent.dbId].length > 1 && (
                      <button onClick={() => scrollReviews('left')} style={{
                        position: 'absolute', left: '-15px', top: '50%', transform: 'translateY(-50%)',
                        background: '#fff', border: '1px solid #eee', borderRadius: '50%', width: '40px', height: '40px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, color: '#003049', fontSize: '18px'
                      }}>❮</button>
                    )}
                    
                    <div ref={reviewsCarouselRef} className="recap-reviews-carousel" style={{ 
                      display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory', 
                      gap: '20px', paddingBottom: '20px', scrollBehavior: 'smooth',
                      msOverflowStyle: 'none', scrollbarWidth: 'none',
                      justifyContent: eventReviews[selectedRecapEvent.dbId].length === 1 ? 'center' : 'flex-start'
                    }}>
                      {eventReviews[selectedRecapEvent.dbId].map((rev, idx) => (
                        <div key={idx} style={{ 
                          minWidth: '320px', maxWidth: '350px', flex: '0 0 auto', scrollSnapAlign: 'start',
                          background: 'rgba(255, 255, 255, 0.7)', 
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          padding: '24px', 
                          borderRadius: '20px', 
                          border: '1px solid rgba(255, 255, 255, 1)',
                          boxShadow: '0 10px 35px rgba(0, 0, 0, 0.05)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                               <div style={{ 
                                 width: '36px', height: '36px', borderRadius: '50%', 
                                 background: 'linear-gradient(135deg, #F07167, #F99B00)', 
                                 display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                 color: 'white', fontWeight: 'bold', fontSize: '15px',
                                 boxShadow: '0 2px 10px rgba(240, 113, 103, 0.3)'
                               }}>
                                 {rev.userName ? rev.userName.charAt(0).toUpperCase() : 'A'}
                               </div>
                               <span style={{ fontWeight: '700', color: '#003049', fontSize: '16px', letterSpacing: '-0.3px' }}>{rev.userName}</span>
                            </div>
                            <span style={{ 
                              fontSize: '20px', background: 'rgba(255,255,255,0.9)', 
                              padding: '6px 10px', borderRadius: '16px', 
                              boxShadow: '0 2px 8px rgba(0,0,0,0.06)' 
                            }}>
                              {rev.rating >= 5 ? '😊' : rev.rating >= 4 ? '🙂' : rev.rating === 3 ? '😐' : rev.rating === 2 ? '🙁' : '😠'}
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '15px', color: '#444', lineHeight: '1.7', fontStyle: 'italic', paddingLeft: '48px' }}>"{rev.reviewText}"</p>
                        </div>
                      ))}
                    </div>

                    {eventReviews[selectedRecapEvent.dbId].length > 1 && (
                      <button onClick={() => scrollReviews('right')} style={{
                        position: 'absolute', right: '-15px', top: '50%', transform: 'translateY(-50%)',
                        background: '#fff', border: '1px solid #eee', borderRadius: '50%', width: '40px', height: '40px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, color: '#003049', fontSize: '18px'
                      }}>❯</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div> {/* Close events-container here */}

      {/* REGISTRATION MODAL */}
      {showModal && selectedEvent && (
        <div className="event-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="event-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="event-modal-close" onClick={() => setShowModal(false)}>✕</button>
            
            <div className="event-modal-body" style={{ justifyContent: 'center', textAlign: 'center', paddingTop: '30px' }}>
              <div className="event-modal-payment" style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                <h3 style={{ marginBottom: '25px', color: '#003049' }}>Registration & Payment</h3>
                
                {selectedEvent.qrCodeImg ? (
                  <div className="qr-code-wrapper">
                    <a href={selectedEvent.paymentLink || selectedEvent.qrCodeLink || selectedEvent.qrCodeImg} target="_blank" rel="noopener noreferrer">
                      <img src={selectedEvent.qrCodeImg} alt="Payment QR" className="qr-code-img" style={{ cursor: 'pointer', margin: '0 auto', marginBottom: '25px' }} />
                    </a>
                    
                    <div>
                      <a href={selectedEvent.paymentLink || selectedEvent.qrCodeLink || selectedEvent.qrCodeImg} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', padding: '14px 28px', background: '#F07167', color: 'white', textDecoration: 'none', borderRadius: '30px', fontWeight: 'bold', fontSize: '15px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)' }}>
                        Scan or Click for Registration
                      </a>
                    </div>
                  </div>
                ) : (
                  <p className="qr-hint">Registration details will be updated soon.</p>
                )}
                {selectedEvent.paymentNumber && (
                  <p><strong>Payment Number:</strong> {selectedEvent.paymentNumber}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* INTEREST SLIDE-OUT PANEL */}
      {showInterestModal && (
        <div className="interest-panel-overlay" onClick={() => { setShowInterestModal(false); setCurrentInterestStep(0); }}>
          <div className="interest-panel" onClick={(e) => e.stopPropagation()}>
            <button className="interest-panel-close" onClick={() => { setShowInterestModal(false); setCurrentInterestStep(0); }}>✕</button>
            
            <div className="interest-panel-content">
              <h3 className="interest-panel-title">Tell Us Your Interests</h3>
              
              <div className="interest-progress-bar">
                <div 
                  className="interest-progress-fill" 
                  style={{ width: `${((currentInterestStep + 1) / interestQuestions.length) * 100}%` }}
                ></div>
              </div>
              <p className="interest-progress-text">
                Step {currentInterestStep + 1} of {interestQuestions.length}
              </p>

              <form onSubmit={handleInterestSubmit} className="interest-step-form">
                
                {/* Step Content */}
                <div className="interest-step-container">
                  <div className="interest-form-group slide-in" key={currentInterestStep}>
                    <label>{interestQuestions[currentInterestStep]}</label>
                    <textarea 
                      required 
                      value={interestAnswers[interestQuestions[currentInterestStep]] || ""} 
                      onChange={(e) => setInterestAnswers({...interestAnswers, [interestQuestions[currentInterestStep]]: e.target.value})} 
                      className="interest-textarea"
                      placeholder="Type your answer here..."
                      autoFocus
                    />
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="interest-panel-actions">
                  {currentInterestStep > 0 && (
                    <button 
                      type="button" 
                      className="interest-nav-btn secondary" 
                      onClick={() => setCurrentInterestStep(prev => prev - 1)}
                    >
                      Back
                    </button>
                  )}
                  
                  {currentInterestStep < (interestQuestions.length - 1) ? (
                    <button 
                      type="button" 
                      className="interest-nav-btn primary" 
                      onClick={() => setCurrentInterestStep(prev => prev + 1)}
                      disabled={!interestAnswers[interestQuestions[currentInterestStep]]}
                    >
                      Next
                    </button>
                  ) : (
                    <button 
                      type="submit" 
                      className="interest-submit-btn" 
                      disabled={isSubmittingInterest || !interestAnswers[interestQuestions[currentInterestStep]]}
                    >
                      {isSubmittingInterest ? 'Submitting...' : 'Complete & Submit'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {/* CINEMATIC VIDEO PLAYER MODAL */}
      {playingVideoUrl && (
        <div className="cinematic-video-overlay" onClick={() => setPlayingVideoUrl(null)}>
          <button className="cinematic-close-btn" onClick={() => setPlayingVideoUrl(null)}>✕</button>
          <div className="cinematic-video-container" onClick={e => e.stopPropagation()}>
            <video 
              src={playingVideoUrl} 
              className="cinematic-video-player"
              controls 
              autoPlay 
              muted
            />
          </div>
        </div>
      )}
    </div>
  );
}
